import {useState, useRef, useEffect, RefObject} from 'react';

import {createLogger} from '~/shared/logging/logging';

const DRAG_THRESHOLD = 6;
const EDGE_PADDING = 8;
const STORAGE_KEY = 'a11y-helper-y';

interface DragState {
  active: boolean;
  startTouchY: number;
  startPosY: number;
  moved: boolean;
  currentY: number | null;
}

interface UseVerticalDragResult {
  posY: number | null;
  rootRef: RefObject<HTMLDivElement>;
  wasDragged: () => boolean;
}

const logger = createLogger('useVerticalDrag');

const clampToViewport = (y: number, buttonSize: number): number =>
  Math.max(EDGE_PADDING, Math.min(window.innerHeight - buttonSize - EDGE_PADDING, y));

const readSavedY = (buttonSize: number): number | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return clampToViewport(Number(raw), buttonSize);
  } catch {
    logger.warn('Failed to read saved vertical drag position');
    return null;
  }
};

const persistY = (y: number): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Math.round(y)));
  } catch {
    logger.warn('Failed to persist vertical drag position');
  }
};

const useVerticalDrag = (enabled: boolean, buttonSize: number): UseVerticalDragResult => {
  const [posY, setPosY] = useState<number | null>(() => (enabled ? readSavedY(buttonSize) : null));
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState>({active: false, startTouchY: 0, startPosY: 0, moved: false, currentY: null});

  useEffect(() => {
    if (!enabled || !rootRef.current) {
      return undefined;
    }

    const el = rootRef.current;

    const onTouchStart = (e: TouchEvent) => {
      drag.current = {
        active: true,
        startTouchY: e.touches[0].clientY,
        startPosY: el.getBoundingClientRect().top,
        moved: false,
        currentY: null,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!drag.current.active) {
        return;
      }

      // Prevent the page from scrolling for any touch that started on this element.
      // Must be here (before the threshold check) — the browser starts the scroll
      // gesture on the first move pixel, before we can detect our own threshold.
      e.preventDefault();

      const delta = e.touches[0].clientY - drag.current.startTouchY;

      if (Math.abs(delta) > DRAG_THRESHOLD) {
        drag.current.moved = true;

        const newY = clampToViewport(drag.current.startPosY + delta, buttonSize);
        drag.current.currentY = newY;
        setPosY(newY);
      }
    };

    const onTouchEnd = () => {
      if (drag.current.moved && drag.current.currentY !== null) {
        persistY(drag.current.currentY);
      }

      drag.current.active = false;
    };

    el.addEventListener('touchstart', onTouchStart, {passive: true});
    el.addEventListener('touchmove', onTouchMove, {passive: false});
    el.addEventListener('touchend', onTouchEnd, {passive: true});

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [enabled, buttonSize]);

  // Returns true (and clears the flag) if the last touch sequence was a drag.
  // Call in onClick to prevent a drag-release from triggering a button action.
  const wasDragged = (): boolean => {
    const result = drag.current.moved;
    drag.current.moved = false;
    return result;
  };

  return {posY, rootRef, wasDragged};
};

export default useVerticalDrag;
